import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminService } from '../../services/loanService';
import { formatCurrency, formatDate } from '../../utils/helpers';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const LOAN_STATUS_META = {
  offer_accepted: { label: 'Awaiting Disbursement', color: '#ff9900' },
  disbursed:      { label: 'Disbursed',             color: '#00C896' },
  active:         { label: 'Active',                color: '#00C896' },
};

function StatusBadge({ value }) {
  const m = LOAN_STATUS_META[value] || { label: value, color: '#94a3b8' };
  return (
    <span style={{
      fontSize: '0.65rem', fontWeight: 700, padding: '0.2rem 0.6rem',
      borderRadius: 99, textTransform: 'uppercase',
      background: `${m.color}18`, color: m.color, border: `1px solid ${m.color}30`,
    }}>{m.label}</span>
  );
}

export default function Disbursements() {
  const [queueType, setQueueType] = useState('pending');

  const { data, isLoading } = useQuery({
    queryKey: ['admin-disbursements', queueType],
    queryFn: () => adminService.getDisbursementQueue(queueType).then(r => r.data.data),
  });

  const items = Array.isArray(data) ? data : [];

  return (
    <div>
      <div className="page-header">
        <h1>Disbursements</h1>
        <p>View all loan disbursements across all statuses</p>
      </div>

      <div className="alert alert-info" style={{ maxWidth: 640, marginBottom: '1.25rem' }}>
        <span>ℹ</span>
        <span>This is a read-only view. Disbursement execution is handled by the Disbursement Officer.</span>
      </div>

      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.25rem' }}>
        {[['pending', 'Pending Queue'], ['disbursed', 'Disbursed']].map(([val, label]) => (
          <button
            key={val}
            className={`btn ${queueType === val ? 'btn-primary' : 'btn-ghost'}`}
            style={{ fontSize: '0.78rem', padding: '0.3rem 0.875rem' }}
            onClick={() => setQueueType(val)}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="card" style={{ padding: 0 }}>
        {isLoading ? <div style={{ padding: '2rem' }}><LoadingSpinner /></div> : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Loan #</th>
                  <th>Applicant</th>
                  <th>Net Disbursement</th>
                  <th>Bank / Account</th>
                  <th>Maker</th>
                  <th>Checker</th>
                  <th>Status</th>
                  <th>Offer Accepted</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={8} style={{ textAlign: 'center', color: 'var(--text-3)', padding: '2rem' }}>
                      No disbursements found.
                    </td>
                  </tr>
                ) : items.map(d => (
                  <tr key={d.id}>
                    <td style={{ fontFamily: 'monospace', fontSize: '0.8rem', color: 'var(--primary)' }}>
                      {d.application_number}
                    </td>
                    <td>
                      <div style={{ fontSize: '0.875rem', fontWeight: 500 }}>{d.applicant_name}</div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-3)' }}>{d.applicant_email}</div>
                    </td>
                    <td style={{ fontWeight: 600 }}>
                      {formatCurrency(d.net_disbursement || d.approved_amount || 0)}
                    </td>
                    <td style={{ fontSize: '0.8rem' }}>
                      {d.bank_name || '—'}
                      <br />
                      <span style={{ color: 'var(--text-3)', fontFamily: 'monospace' }}>
                        {d.account_number || '—'}
                      </span>
                    </td>
                    <td style={{ fontSize: '0.8rem' }}>{d.maker_name || '—'}</td>
                    <td style={{ fontSize: '0.8rem' }}>{d.checker_name || '—'}</td>
                    <td><StatusBadge value={d.status} /></td>
                    <td style={{ fontSize: '0.8rem', color: 'var(--text-2)' }}>
                      {formatDate(d.offer_accepted_at || d.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
